import { Colors } from '@oy/uicore';
import {
  IconName,
  ProductFeatureIcon,
  ProductFeatureIconName,
  SystemIcon,
} from '@oy/uiicon';
import React, { Ref, forwardRef } from 'react';
import {
  formatPascalCase,
  formatString,
} from '../../../../utilities/idComponent';
import { Spacer } from '../../../Layout';
import Button, { ButtonProps } from '../Button';

export type ButtonWithIconProps = {
  iconName: IconName;
  productFeatureIconName?: ProductFeatureIconName;
  /** Allow dedupe mechanism, default is false */
  isDedupe?: boolean;
  dedupeValue?: number;
} & ButtonProps;

const ICON_COLORS = {
  [Button.Color.Primary]: Colors.BLUE100,
  [Button.Color.Secondary]: Colors.BLACK100,
  [Button.Color.Destructive]: Colors.RED100,
};

const ICON_SIZES = {
  [Button.Size.Small]: 16,
  [Button.Size.Medium]: 20,
  [Button.Size.Large]: 24,
};

const ButtonWithIcon = forwardRef<HTMLButtonElement, ButtonWithIconProps>(
  (
    {
      children,
      iconName,
      productFeatureIconName,
      buttonType = Button.Type.Filled,
      color = Button.Color.Primary,
      size = Button.Size.Medium,
      isDedupe = false,
      dedupeValue = 500,
      ...buttonProps
    }: ButtonWithIconProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const iconColor =
      buttonType === Button.Type.Filled
        ? Colors.WHITE
        : ICON_COLORS[color as keyof typeof ICON_COLORS];

    const iconSize = ICON_SIZES[size];

    const childrenSpacer =
      size === Button.Size.Large ? Spacer.Size.S : Spacer.Size.XS;

    return (
      <Button
        {...buttonProps}
        buttonType={buttonType}
        color={color}
        ref={ref}
        size={size}
        name={
          formatString(children) ?? formatPascalCase(iconName + 'Component')
        }
        isDedupe={isDedupe}
        dedupeValue={dedupeValue}
      >
        {productFeatureIconName ? (
          <ProductFeatureIcon
            color={iconColor}
            name={productFeatureIconName}
            size={iconSize}
          />
        ) : (
          <SystemIcon color={iconColor} name={iconName} size={iconSize} />
        )}
        {children && <Spacer left={childrenSpacer}>{children}</Spacer>}
      </Button>
    );
  },
);

export default Object.assign(ButtonWithIcon, {
  Size: Button.Size,
  Type: Button.Type,
  Color: Button.Color,
});
